import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { SlRadioGroup } from '@shoelace-style/shoelace';
import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { linkStyles } from './cssstyles.js';

export class ProjectVisibilityEvent extends Event {
  constructor(
    public readonly visibility: WireTypes.ProjectVisibility,
  ) {
    super('project-visibility', {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
  }
}

@customElement('pick-project-visibility')
export class PickProjectVisibility extends LitElement {

  @property({ attribute: false }) initial?: WireTypes.ProjectVisibility;
  @property({ type: Boolean }) disabled?: boolean;
  @property({ attribute: false }) allowedVisibilities?: WireTypes.ProjectVisibility[];

  @query('sl-radio-group') private _visibilityInput?: SlRadioGroup;

  private _onChange() {
    const visibility = this.visibility();
    if (visibility)
      this.dispatchEvent(new ProjectVisibilityEvent(visibility));
  }

  private _isDisabled(value: WireTypes.ProjectVisibility): boolean {
    if (this.disabled)
      return true;
    if (!this.allowedVisibilities)
      return false;
    return !this.allowedVisibilities.includes(value);
  }

  private _effectiveInitial(): WireTypes.ProjectVisibility {
    if (this.initial)
      return this.initial;
    if (!this._isDisabled('private'))
      return 'private';
    return 'public';
  }

  visibility(): WireTypes.ProjectVisibility {
    return (this._visibilityInput?.value as WireTypes.ProjectVisibility|undefined) ?? this._effectiveInitial();
  }

  override render() {
    const planNotice = html`<div class=plan-notice>Not available in your plan</div>`;
    return html`
      <sl-radio-group @sl-input=${this._onChange} name="a" value=${this._effectiveInitial()}>
        <sl-radio ?disabled=${this._isDisabled('private')} value=${'private' satisfies WireTypes.ProjectVisibility}>
          <h-box style="gap: var(--sl-spacing-2x-small);"><project-visibility value=private></project-visibility>Private</h-box>
          <div class=description>Organization members and project collaborators have access. For GitHub-connected projects, repository collaborators are also granted Viewer access automatically.</div>
          ${this._isDisabled('private') ? planNotice : ''}
        </sl-radio>
        <sl-radio ?disabled=${this._isDisabled('public')} value=${'public' satisfies WireTypes.ProjectVisibility}>
          <h-box style="gap: var(--sl-spacing-2x-small);"><project-visibility value=public></project-visibility>Public</h-box>
          <div class=description>The project can be accessed without any authentication.</div>
          ${this._isDisabled('public') ? planNotice : ''}
        </sl-radio>
      </sl-radio-group>
    `;
  }

  static styles = [linkStyles, css`
    sl-radio + sl-radio {
      margin-top: var(--sl-spacing-small);
    }

    .description {
      color: var(--sl-color-neutral-500);
    }

    .plan-notice {
      color: var(--sl-color-warning-600);
      font-size: var(--sl-font-size-small);
      font-style: italic;
    }
  `];
}
